<script lang="ts">
  /* «Начало работы» v2 — the grouped setup checklist (spec 20260909 §3).

     Groups are ordered by the path to the first order and disclosed
     progressively: only the current group (the first with something left to
     do) is open, finished groups fold to a line, the next one opens by itself.
     The server computes done/skipped/locked (`lib/onboardingChecklist`), so
     the Dashboard, Settings and the wizard's closing screen agree.

     Closing the panel minimises it to a one-line summary rather than
     dismissing it: the summary bar and the Dashboard's «Руководство по
     настройке» button both reopen it. The post-goal upsell below is the one
     part that does dismiss for good. */
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import { push } from "svelte-spa-router";
  import InfoTooltip from "$components/InfoTooltip.svelte";
  import OnboardingServiceCard from "$components/OnboardingServiceCard.svelte";
  import { auth } from "$lib/auth";
  import { formatDate } from "$lib/format";
  import { isPro } from "$lib/permissions";
  import {
    checklist,
    collapseChecklist,
    currentGroupId,
    doneCount as countDone,
    expandChecklist,
    loadChecklist,
    percentComplete,
    skipItem,
    totalCount,
    type SetupGroup,
    type SetupItem,
  } from "$lib/onboardingChecklist";

  let {
    tenantSlug,
    /** Set by the Dashboard's guide button so the panel opens even after
     *  every step is done — otherwise the button would do nothing. */
    forceOpen = false,
    /** Settings embeds the full checklist as a durable step-by-step manual. */
    embedded = false,
    /** The wizard's closing screen: just the current group, no chrome. */
    firstGroupOnly = false,
  }: { tenantSlug: string; forceOpen?: boolean; embedded?: boolean; firstGroupOnly?: boolean } = $props();

  onMount(async () => {
    try {
      upgradeDismissed = localStorage.getItem(`oe_onboard_upgrade_dismissed_${tenantSlug}`) === "1";
    } catch { /* ignore */ }
    // Force a refetch: the Dashboard is where progress most visibly changes.
    await loadChecklist(tenantSlug, true);
  });

  const cl = $derived($checklist.slug === tenantSlug ? $checklist : null);
  const loaded = $derived(!!cl?.loaded && !!cl?.status);
  const status = $derived(cl?.status ?? null);
  const groups = $derived<SetupGroup[]>(status?.groups ?? []);
  const doneCount = $derived(cl ? countDone(cl) : 0);
  const total = $derived(cl ? totalCount(cl) : 0);
  const pct = $derived(cl ? percentComplete(cl) : 0);
  const allDone = $derived(loaded && total > 0 && doneCount === total);
  const collapsed = $derived(!!cl?.collapsed);

  // Which group is unfolded. Follows the server's "current" group until the
  // user toggles one by hand; a fresh payload with a new current group (a step
  // just got done) takes over again.
  let openId = $state<string | null>(null);
  let pinnedBy = $state<string | null>(null);
  $effect(() => {
    const cur = currentGroupId(status);
    if (pinnedBy === null || pinnedBy === cur) openId = cur ?? groups[0]?.id ?? null;
  });
  function toggle(id: string) {
    const cur = currentGroupId(status);
    if (openId === id) {
      openId = null;
      pinnedBy = "__closed__";
    } else {
      openId = id;
      pinnedBy = id === cur ? null : cur ?? "__none__";
    }
  }

  const visibleGroups = $derived(
    firstGroupOnly ? groups.filter((g) => g.id === (openId ?? currentGroupId(status))).slice(0, 1) : groups,
  );

  // Full panel: setup work outstanding (or explicitly reopened) and not minimised.
  const expanded = $derived(loaded && (embedded || firstGroupOnly || (!collapsed && (!allDone || forceOpen))));
  // Summary bar: the panel exists but is minimised.
  const showSummary = $derived(!embedded && !firstGroupOnly && loaded && collapsed && (!allDone || forceOpen));

  const pro = $derived(isPro($auth, tenantSlug));
  const upgradeDismissKey = $derived(`oe_onboard_upgrade_dismissed_${tenantSlug}`);
  let upgradeDismissed = $state(false);
  const showUpgrade = $derived(
    !embedded && !firstGroupOnly && allDone && !pro &&
      !!$auth.memberships.find((m) => m.tenant.slug === tenantSlug)?.capabilities?.capabilities.includes("seller") &&
      !upgradeDismissed,
  );

  const trialDate = $derived(status?.trial_ends_at ? formatDate(status.trial_ends_at) : "");

  function go(item: SetupItem) {
    if (item.locked) {
      push(`/t/${tenantSlug}/account/tariff`);
      return;
    }
    push(item.href);
  }
  let busy = $state<string | null>(null);
  async function toggleSkip(item: SetupItem) {
    busy = item.id;
    try {
      await skipItem(tenantSlug, item.id, !item.skipped);
    } catch {
      /* no permission or offline — the row simply stays as it was */
    } finally {
      busy = null;
    }
  }
  function collapse() { collapseChecklist(tenantSlug); }
  function expand() { expandChecklist(tenantSlug); }
  function dismissUpgrade() {
    upgradeDismissed = true;
    try { localStorage.setItem(upgradeDismissKey, "1"); } catch { /* ignore */ }
  }
</script>

{#if showSummary}
  <button type="button" class="onboard-summary card" onclick={expand}>
    <span class="material-icons-outlined summary-icon" aria-hidden="true">rocket_launch</span>
    <span class="summary-text">{$_("onboard.title")}</span>
    <span class="summary-bar" aria-hidden="true">
      <span class="summary-fill" style={`width: ${pct}%`}></span>
    </span>
    <span class="summary-progress">
      {$_("onboard.progress", { values: { done: doneCount, total } })}
    </span>
    <span class="summary-open">
      {$_("onboard.expand")}
      <span class="material-icons-outlined" aria-hidden="true">expand_more</span>
    </span>
  </button>
{/if}

{#if expanded}
  <section class="onboard card" class:bare={firstGroupOnly} aria-label={$_("onboard.title")}>
    {#if !firstGroupOnly}
      <div class="onboard-head">
        <div>
          <h2 class="onboard-title">{$_("onboard.title")}</h2>
          <p class="onboard-sub">{$_(`setup.subtitle.${status?.role ?? "seller"}`)}</p>
        </div>
        <div class="onboard-head-right">
          <span class="onboard-progress">{$_("onboard.progress", { values: { done: doneCount, total } })}</span>
          {#if !embedded}
            <button type="button" class="onboard-dismiss" onclick={collapse} title={$_("onboard.collapse")} aria-label={$_("onboard.collapse")}>
              <span class="material-icons-outlined">expand_less</span>
            </button>
          {/if}
        </div>
      </div>
    {/if}

    <ol class="groups">
      {#each visibleGroups as group (group.id)}
        {@const idx = groups.indexOf(group) + 1}
        {@const isOpen = firstGroupOnly || openId === group.id}
        <li class="group" class:open={isOpen} class:complete={group.complete}>
          <button type="button" class="group-head" onclick={() => !firstGroupOnly && toggle(group.id)} aria-expanded={isOpen}>
            <span class="group-marker" aria-hidden="true">
              {#if group.complete}
                <span class="material-icons-outlined">check</span>
              {:else}
                {idx}
              {/if}
            </span>
            <span class="group-title">{$_(`setup.group.${group.id}.title`)}</span>
            {#if group.items.some((i) => i.once)}
              <span class="chip chip--once">{$_("setup.chip.once")}</span>
            {/if}
            {#if group.items.some((i) => i.pro)}
              <span class="chip chip--pro">{$_("setup.chip.pro")}</span>
            {/if}
            <span class="group-progress">{$_("setup.groupProgress", { values: { done: group.done, total: group.total } })}</span>
            {#if !firstGroupOnly}
              <span class="material-icons-outlined group-chevron" aria-hidden="true">expand_more</span>
            {/if}
          </button>

          {#if isOpen}
            {#if group.id === "open"}
              <p class="group-note">{$_("setup.group.open.note")}</p>
            {/if}
            <ol class="items">
              {#each group.items as item, i (item.id)}
                {@const closed = item.done || item.skipped}
                <li class="item" class:done={item.done} class:skipped={item.skipped && !item.done} class:locked={item.locked && !item.done}>
                  <span class="item-marker" aria-hidden="true">
                    {#if item.done}
                      <span class="material-icons-outlined">check</span>
                    {:else if item.skipped}
                      <span class="material-icons-outlined">remove</span>
                    {:else}
                      {i + 1}
                    {/if}
                  </span>
                  <div class="item-body">
                    <div class="item-title-row">
                      <span class="item-title">{$_(`setup.item.${item.id}.title`)}</span>
                      {#if item.once}
                        <span class="chip chip--once">{$_("setup.chip.once")}</span>
                        <InfoTooltip text={$_("setup.once.hint")} />
                      {/if}
                      {#if !item.required}
                        <span class="chip chip--opt">{$_("setup.chip.optional")}</span>
                      {/if}
                      {#if item.pro && !item.done}
                        {#if item.locked}
                          <span class="chip chip--locked"><span class="material-icons-outlined" aria-hidden="true">lock</span>{$_("setup.chip.locked")}</span>
                        {:else if trialDate}
                          <span class="chip chip--pro">{$_("setup.chip.trial", { values: { date: trialDate } })}</span>
                        {/if}
                      {/if}
                    </div>
                    <div class="item-why">{$_(`setup.item.${item.id}.why`)}</div>
                  </div>
                  <div class="item-actions">
                    {#if item.done}
                      <span class="item-done-label">{$_("onboard.stepDone")}</span>
                    {:else if item.skipped}
                      <span class="item-skipped-label">{$_("setup.skipped")}</span>
                      <button type="button" class="link-btn" disabled={busy === item.id} onclick={() => toggleSkip(item)}>{$_("setup.unskip")}</button>
                    {:else}
                      {#if !item.required}
                        <button type="button" class="link-btn" disabled={busy === item.id} onclick={() => toggleSkip(item)}>{$_("setup.skip")}</button>
                      {/if}
                      <button type="button" class="btn btn--primary btn--sm" onclick={() => go(item)}>
                        {item.locked ? $_("setup.locked.cta") : $_(item.cta)} →
                      </button>
                    {/if}
                  </div>
                  {#if closed}<span class="sr-only"></span>{/if}
                </li>
              {/each}
            </ol>
          {/if}
        </li>
      {/each}
    </ol>
  </section>
{/if}

<!-- «Начало работы» in Settings also offers the paid personal onboarding
     and, once ordered, shows its progress (spec 20261001 §8). -->
{#if embedded && !firstGroupOnly}
  <OnboardingServiceCard slug={tenantSlug} />
{/if}

{#if showUpgrade}
  <section class="onboard-upsell card" aria-label={$_("onboard.upgrade.title")}>
    <span class="material-icons-outlined upsell-icon">workspace_premium</span>
    <div class="upsell-copy">
      <div class="upsell-title">{$_("onboard.upgrade.title")}</div>
      <div class="upsell-desc">{$_("onboard.upgrade.body")}</div>
    </div>
    <button type="button" class="btn btn--primary btn--sm" onclick={() => push(`/t/${tenantSlug}/pricing`)}>
      {$_("onboard.upgrade.cta")} →
    </button>
    <button type="button" class="onboard-dismiss" onclick={dismissUpgrade} aria-label={$_("onboard.dismiss")}>
      <span class="material-icons-outlined">close</span>
    </button>
  </section>
{/if}

<style>
  .onboard {
    background: #fff;
    border: 1px solid var(--color-divider);
    border-radius: var(--radius);
    border-left: 4px solid var(--color-primary);
    padding: 18px 20px;
  }
  .onboard.bare { border: none; padding: 0; background: transparent; }

  .onboard-summary {
    display: flex; align-items: center; gap: 12px; width: 100%;
    background: #fff; border: 1px solid var(--color-divider);
    border-left: 4px solid var(--color-primary); border-radius: var(--radius);
    padding: 11px 16px; cursor: pointer; font: inherit; text-align: left;
    transition: box-shadow 0.12s, border-color 0.12s;
  }
  .onboard-summary:hover { box-shadow: 0 4px 14px rgba(0,0,0,0.07); }
  .summary-icon { color: var(--color-primary); font-size: 20px; flex-shrink: 0; }
  .summary-text { font-size: 14px; font-weight: 600; color: var(--color-surface-darker); flex-shrink: 0; }
  .summary-bar { flex: 1; min-width: 60px; max-width: 220px; height: 6px; background: var(--color-bg); border-radius: 3px; overflow: hidden; }
  .summary-fill { display: block; height: 100%; background: var(--color-primary); transition: width 0.2s; }
  .summary-progress { font-size: 12px; font-weight: 600; color: var(--color-primary); white-space: nowrap; }
  .summary-open { display: inline-flex; align-items: center; gap: 2px; margin-left: auto; font-size: 12px; font-weight: 600; color: var(--color-text-muted); white-space: nowrap; }
  .summary-open .material-icons-outlined { font-size: 18px; }
  .onboard-summary:hover .summary-open { color: var(--color-primary); }
  @media (max-width: 700px) { .summary-bar, .summary-progress { display: none; } }

  .onboard-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; margin-bottom: 14px; }
  .onboard-title { margin: 0; font-size: 17px; font-weight: 700; color: var(--color-surface-darker); }
  .onboard-sub { margin: 3px 0 0; font-size: 13px; color: var(--color-text-muted); }
  .onboard-head-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
  .onboard-progress { font-size: 12px; font-weight: 600; color: var(--color-primary); white-space: nowrap; }
  .onboard-dismiss {
    display: inline-flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border: none; background: none; cursor: pointer;
    color: var(--color-text-muted); border-radius: var(--radius);
  }
  .onboard-dismiss:hover { background: var(--color-bg); color: var(--color-text); }
  .onboard-dismiss .material-icons-outlined { font-size: 18px; }

  /* Groups */
  .groups { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
  .group { border: 1px solid var(--color-divider); border-radius: var(--radius); background: #fff; overflow: hidden; }
  .group.complete { background: rgba(46,158,91,0.04); border-color: rgba(46,158,91,0.25); }
  .group.open { border-color: rgba(33,133,213,0.45); }
  .group-head {
    display: flex; align-items: center; gap: 10px; width: 100%; text-align: left;
    padding: 11px 14px; border: none; background: none; cursor: pointer; font: inherit; color: inherit;
  }
  .group-head:focus-visible { outline: 2px solid var(--color-primary); outline-offset: -2px; }
  .group-marker {
    flex-shrink: 0; width: 24px; height: 24px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--color-primary); color: #fff; font-size: 12px; font-weight: 700;
  }
  .group.complete .group-marker { background: #2E9E5B; }
  .group-marker .material-icons-outlined { font-size: 16px; }
  .group-title { font-size: 14px; font-weight: 700; color: var(--color-surface-darker); flex: 1; min-width: 0; }
  .group.complete .group-title { color: var(--color-text-muted); font-weight: 600; }
  .group-progress { font-size: 12px; font-weight: 600; color: var(--color-text-muted); white-space: nowrap; }
  .group-chevron { color: var(--color-text-muted); font-size: 20px; transition: transform 0.15s; }
  .group.open .group-chevron { transform: rotate(180deg); }
  .group-note {
    margin: 0; padding: 8px 14px 10px 48px; font-size: 12.5px; color: var(--color-text-muted); line-height: 1.45;
    background: var(--color-bg);
  }

  /* Items */
  .items { list-style: none; margin: 0; padding: 4px 10px 10px; display: flex; flex-direction: column; gap: 6px; }
  .item { display: flex; align-items: center; gap: 12px; padding: 10px 12px; border: 1px solid var(--color-divider); border-radius: var(--radius); }
  .item.done { background: rgba(46,158,91,0.06); border-color: rgba(46,158,91,0.25); }
  .item.skipped { background: var(--color-bg); }
  .item.locked { border-style: dashed; }
  .item-marker {
    flex-shrink: 0; width: 22px; height: 22px; border-radius: 50%;
    display: inline-flex; align-items: center; justify-content: center;
    background: var(--color-bg); color: var(--color-text-muted); font-size: 12px; font-weight: 700;
  }
  .item.done .item-marker { background: #2E9E5B; color: #fff; }
  .item-marker .material-icons-outlined { font-size: 15px; }
  .item-body { flex: 1; min-width: 0; }
  .item-title-row { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
  .item-title { font-size: 14px; font-weight: 600; color: var(--color-surface-darker); }
  .item.done .item-title, .item.skipped .item-title { color: var(--color-text-muted); }
  .item-why { font-size: 12px; color: var(--color-text-muted); margin-top: 2px; line-height: 1.4; }
  .item-actions { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
  .item-done-label { font-size: 12px; font-weight: 600; color: #2E9E5B; }
  .item-skipped-label { font-size: 12px; font-weight: 600; color: var(--color-text-muted); }
  @media (max-width: 640px) {
    .item { flex-wrap: wrap; }
    .item-actions { width: 100%; justify-content: flex-end; }
  }

  .chip {
    display: inline-flex; align-items: center; gap: 3px;
    font-size: 10.5px; font-weight: 700; letter-spacing: 0.03em; text-transform: uppercase;
    padding: 2px 7px; border-radius: 999px; border: 1px solid var(--color-divider);
    color: var(--color-text-muted); white-space: nowrap; line-height: 1.4;
  }
  .chip .material-icons-outlined { font-size: 12px; }
  .chip--once { border-color: rgba(46,158,91,0.45); color: #2E9E5B; background: rgba(46,158,91,0.07); text-transform: none; letter-spacing: 0; }
  .chip--pro { border-color: rgba(33,133,213,0.45); color: var(--color-primary); background: rgba(33,133,213,0.07); text-transform: none; letter-spacing: 0; }
  .chip--opt { border-style: dashed; text-transform: none; letter-spacing: 0; }
  .chip--locked { border-color: rgba(183,121,31,0.5); color: #9a6413; background: rgba(183,121,31,0.08); text-transform: none; letter-spacing: 0; }

  .btn { display: inline-flex; align-items: center; gap: 4px; border: none; border-radius: var(--radius); cursor: pointer; font-weight: 600; font-family: inherit; }
  .btn--primary { background: var(--color-primary); color: #fff; }
  .btn--sm { padding: 7px 14px; font-size: 13px; white-space: nowrap; }
  .link-btn { border: none; background: none; padding: 0; font: inherit; font-size: 12.5px; font-weight: 600; color: var(--color-text-muted); cursor: pointer; text-decoration: underline; text-underline-offset: 2px; }
  .link-btn:hover { color: var(--color-primary); }
  .link-btn:disabled { opacity: 0.5; cursor: default; }
  .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); }

  .onboard-upsell {
    display: flex; align-items: center; gap: 14px;
    background: linear-gradient(135deg, rgba(33,133,213,0.08), rgba(33,133,213,0.03));
    border: 1px solid rgba(33,133,213,0.3);
    border-left: 4px solid var(--color-primary);
    border-radius: var(--radius);
    padding: 16px 18px;
  }
  .onboard-upsell .upsell-icon { color: var(--color-primary); font-size: 28px; flex-shrink: 0; }
  .upsell-copy { flex: 1; min-width: 0; }
  .onboard-upsell .upsell-title { font-size: 15px; font-weight: 700; color: var(--color-surface-darker); }
  .onboard-upsell .upsell-desc { font-size: 13px; color: var(--color-text-muted); margin-top: 2px; }
</style>
